import { Tabs } from 'antd';
import { Navigate, useNavigate } from 'react-router';
import { PageHeader } from '../../../shared/components/PageHeader';
import { sectionTrail } from '../../../shared/components/section-trail';
import { RateCardsListTab } from '../components/RateCardsListTab';
import { RateLineApprovalQueuePage } from '../features/approvals/pages/RateLineApprovalQueuePage';
import { OffcardReportPage } from '../features/offcard-report/pages/OffcardReportPage';
import { useRateCardPermissions } from '../hooks/use-rate-card-permissions';
import { RATE_CARDS_TAB_LABELS, rateCardsTabPath, type RateCardsTab } from '../rate-cards.paths';

const TAB_BODIES: Record<RateCardsTab, () => React.JSX.Element> = {
  cards: RateCardsListTab,
  approvals: RateLineApprovalQueuePage,
  'off-card-report': OffcardReportPage,
};

/** The Rate cards section's landing page: the cards, the approval queue and the off-card report, one route each. */
export function RateCardsPage({ tab }: { tab: RateCardsTab }) {
  const navigate = useNavigate();
  const { canApprove, canViewOffcardReport } = useRateCardPermissions();

  const allowed: Record<RateCardsTab, boolean> = { cards: true, approvals: canApprove, 'off-card-report': canViewOffcardReport };
  if (!allowed[tab]) return <Navigate to={rateCardsTabPath('cards')} replace />;
  // The old header button was the pricing desk's; someone with only `job.view` still opens the report by its URL, as before, but isn't offered it.
  const listed: Record<RateCardsTab, boolean> = { ...allowed, 'off-card-report': canApprove && canViewOffcardReport };

  const Body = TAB_BODIES[tab];
  const items = (Object.keys(listed) as RateCardsTab[]).filter((key) => listed[key] || key === tab).map((key) => ({ key, label: RATE_CARDS_TAB_LABELS[key] }));

  return (
    <>
      <PageHeader breadcrumbs={sectionTrail('rate-cards')} title="Rate cards" />
      <Tabs activeKey={tab} onChange={(key) => navigate(rateCardsTabPath(key as RateCardsTab))} items={items} />
      <Body key={tab} />
    </>
  );
}
